import React from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import metaLogo from '../../asset/images/icons/platform/meta-svgrepo-com.svg';
import tiktokLogo from '../../asset/images/icons/platform/tiktok-svgrepo-com.svg';
import youtubeLogo from '../../asset/images/icons/platform/youtube-168-svgrepo-com.svg';
import verifiedBadge from '../../asset/images/icons/verified_badge.svg';

const PLATFORMS = [
    { id: 'meta', name: 'Meta', logo: metaLogo },
    { id: 'tiktok', name: 'TikTok', logo: tiktokLogo },
    { id: 'youtube', name: 'YouTube', logo: youtubeLogo },
] as const;

function IntegrationRow({
    name,
    logo,
    nameColor,
    subtitleColor,
    compact,
}: {
    name: string;
    logo: string;
    nameColor: string;
    subtitleColor: string;
    compact?: boolean;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: compact ? { xs: 0.5, sm: 1.25 } : { xs: 1.5, sm: 2 },
                py: 0.25,
                minWidth: 0,
                width: '100%',
            }}>
            <Box
                component="img"
                src={logo}
                alt=""
                aria-hidden
                sx={{
                    width: compact ? { xs: 14, sm: 18 } : 28,
                    height: compact ? { xs: 14, sm: 18 } : 28,
                    flexShrink: 0,
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)',
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: compact ? { xs: 0.35, sm: 0.5 } : 0.75,
                        minWidth: 0,
                    }}>
                    <Typography
                        noWrap
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 700,
                            fontSize: compact ? { xs: 9, sm: 13 } : { xs: 14, sm: 16 },
                            color: nameColor,
                            lineHeight: 1.15,
                            letterSpacing: '-0.01em',
                        }}>
                        {name}
                    </Typography>
                    <Box
                        component="img"
                        src={verifiedBadge}
                        alt="Verified integration"
                        sx={{
                            width: compact ? { xs: 15, sm: 20 } : 26,
                            height: compact ? { xs: 15, sm: 20 } : 26,
                            flexShrink: 0,
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: compact ? { xs: 5.5, sm: 8 } : 10,
                        fontWeight: 600,
                        letterSpacing: compact ? { xs: '0.04em', sm: '0.1em' } : '0.1em',
                        textTransform: 'uppercase',
                        color: subtitleColor,
                        mt: 0.15,
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}>
                    API INTEGRATION APPROVED
                </Typography>
            </Box>
        </Box>
    );
}

export interface PlatformApiIntegrationTrustProps {
    /** Horizontal row of all platforms (default) vs vertical stack */
    layout?: 'row' | 'stack';
    compact?: boolean;
    sx?: SxProps<Theme>;
}

/** Trust strip — official Meta / TikTok / YouTube OAuth integrations (no card borders). */
export function PlatformApiIntegrationTrust({ layout = 'row', compact = false, sx }: PlatformApiIntegrationTrustProps) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: layout === 'row' ? 'row' : 'column',
                flexWrap: layout === 'row' ? 'nowrap' : undefined,
                alignItems: layout === 'row' ? 'flex-end' : 'stretch',
                justifyContent: layout === 'row' ? 'flex-end' : undefined,
                gap: layout === 'row' ? { xs: compact ? 0.75 : 1.5, sm: compact ? 2 : 3, md: compact ? 2.5 : 4 } : 2.5,
                width: '100%',
                ...sx,
            }}>
            {PLATFORMS.map(p => (
                <Box
                    key={p.id}
                    sx={{
                        flex: layout === 'row' ? '1 1 0' : undefined,
                        minWidth: layout === 'row' ? 0 : undefined,
                    }}>
                    <IntegrationRow
                        name={p.name}
                        logo={p.logo}
                        nameColor={t.true_white}
                        subtitleColor={t.subtle_white ?? t.grey}
                        compact={compact}
                    />
                </Box>
            ))}
        </Box>
    );
}
